// ---------------------------------------------------------------------------
// client-report-renderer.ts — Renders a ClientReportModel into a multi-page
// HTML document string for the branded client report preview.
//
// TWO HOSTS. Bundled into the browser AND dynamically imported by the Functions
// host to render the project-risk.com report server-side. Product identity
// comes from the browser accessor (app-common/product-configuration), which has
// no injected product name in Node and so resolves to GamePlan on the funnel.
//
// That is why the cover's site line below is the literal WWW.BUILDERPLAN.COM
// and not productWebsiteHost(config): the literal is DELIBERATE. A config read
// would make the funnel's report say "gameplan.global", which is strictly worse
// than today. Do not "fix" it until these renderers are given a product
// configuration by their caller instead of reaching for a browser global.
// ---------------------------------------------------------------------------

import type { ClientReportModel, NormalizedRisk, RiskBand } from './types';
import { deriveRiskBand } from './risk-utils';
import { gameplanProductConfiguration } from '../../../app-common/product-configuration';
import { escapeHtml as escHtml } from '#gp/app-common/util/html-escape';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface RenderClientReportOptions {
  generatePreviewOnly?: boolean;
}

export function renderClientReportDocument(
  model: ClientReportModel,
  options?: RenderClientReportOptions,
): string {
  const generatePreviewOnly = options?.generatePreviewOnly === true;
  const executiveBrief =
    model.executiveBriefText ||
    extractSupportingAnalysisSection(model.appendix.analysisNarrative, 'Executive brief');
  const strengthsObserved = extractSupportingAnalysisSection(model.appendix.analysisNarrative, 'Strengths Observed');
  const appendixAnalysisWithoutExecutiveBrief = removeSupportingAnalysisSection(
    removeSupportingAnalysisSection(model.appendix.analysisNarrative, 'Executive brief'),
    'Strengths Observed',
  );
  const executiveBriefHtml = renderExecutiveBriefSection(model, executiveBrief);
  const strengthsObservedHtml = renderSupportingAnalysisHtml(strengthsObserved);

  const kpiLabels = new Set([
    'Total Tasks', 'Total Activities',
    'Task Completion', 'Activity Completion',
    'Not Started Tasks', 'Not Started Activities',
    'Longest Critical Chain',
  ]);
  const excludedDetailLabels = new Set(['Critical Path Coverage']);
  const kpiFacts = model.scheduleFacts.filter(fact => kpiLabels.has(fact.label));
  const detailFacts = model.scheduleFacts.filter(
    fact => !kpiLabels.has(fact.label) && !excludedDetailLabels.has(fact.label),
  );

  const kpiHtml = kpiFacts.map(fact => {
    const isLongestChain = fact.label === 'Longest Critical Chain';
    const valueHtml = isLongestChain
      ? formatLongestChainValue(fact.value)
      : escHtml(fact.value);
    return (
      '<article class="client-report-kpi-card"><span>' + escHtml(fact.label) + '</span><strong' +
      (isLongestChain ? ' class="client-report-kpi-value-stacked"' : '') +
      '>' + valueHtml + '</strong></article>'
    );
  }).join('');

  const detailFactsHtml = detailFacts.map(fact =>
    '<div class="client-report-fact"><dt>' + escHtml(fact.label) + '</dt><dd>' + escHtml(fact.value) + '</dd></div>',
  ).join('');

  const criticalPathNote = model.scheduleFacts.find(fact => fact.label === 'Critical Path Coverage');

  const appendixGroups = model.appendix.groups.length
    ? model.appendix.groups.map((group, index) => {
      const count = Number(group.numberOfTasks || 0);
      const unitLabel = count === 1 ? model.appendix.groupItemSingular : model.appendix.groupItemPlural;
      return [
        '<li class="client-report-ranked-item">',
        '<span class="client-report-ranked-index">' + escHtml(String(index + 1)) + '</span>',
        '<span class="client-report-ranked-name">' + escHtml(group.name) + '</span>',
        '<span class="client-report-ranked-value">' + escHtml(String(count)) + ' ' + escHtml(unitLabel) + '</span>',
        '</li>',
      ].join('');
    }).join('')
    : '<li class="client-report-ranked-item client-report-ranked-item-empty"><span class="client-report-ranked-name">No group summary data available.</span></li>';

  const previewRiskCards = generatePreviewOnly
    ? model.riskRegister.slice(0, 1)
    : model.riskRegister;
  const riskCardsHtml = previewRiskCards.length
    ? previewRiskCards.map(renderTopRiskCard).join('')
    : '<div class="client-report-note">No structured risks were available in the latest saved intelligence report.</div>';

  const parts: string[] = [
    '<article class="client-report-document">',
    renderCover(model),
    '<section class="client-report-page client-report-page-break">',
    '<h2 class="client-report-section-title client-report-section-title-first client-report-section-title-metrics">Schedule Metrics</h2>',
    '<div class="client-report-kpi-grid">' + kpiHtml + '</div>',
    '<dl class="client-report-facts-grid client-report-facts-grid-detailed">' + detailFactsHtml + '</dl>',
    (criticalPathNote ? '<div class="client-report-summary-note"><strong>Critical Path Coverage:</strong> ' + escHtml(simplifyCriticalPathCoverageNote(criticalPathNote.value)) + '</div>' : ''),
    '</section>',
    '<section class="client-report-page client-report-page-break">',
    (executiveBriefHtml ? '<div class="client-report-section-eyebrow">Executive Brief</div><section class="client-report-executive-summary">' + executiveBriefHtml + '</section>' : ''),
    '<div class="client-report-section-eyebrow client-report-section-eyebrow-spaced">Overall Risk Assessment</div>',
    '<div class="client-report-assessment-panel">',
    '<div class="client-report-risk-rating client-report-risk-rating-' + escHtml(model.overallRisk.rating) + '">' + escHtml(model.overallRisk.rating.toUpperCase()) + '</div>',
    '<p class="client-report-assessment-text">' + escHtml(model.overallRisk.narrative) + '</p>',
    '<div class="client-report-scorecard">',
    '<div><span>High priority</span><strong>' + escHtml(String(model.riskRegister.filter(risk => deriveRiskBand(risk) === 'high').length)) + '</strong></div>',
    '<div><span>Medium-High priority</span><strong>' + escHtml(String(model.riskRegister.filter(risk => deriveRiskBand(risk) === 'medium-high').length)) + '</strong></div>',
    '<div><span>Medium priority</span><strong>' + escHtml(String(model.riskRegister.filter(risk => deriveRiskBand(risk) === 'medium').length)) + '</strong></div>',
    '<div><span>Medium-Low priority</span><strong>' + escHtml(String(model.riskRegister.filter(risk => deriveRiskBand(risk) === 'medium-low').length)) + '</strong></div>',
    '<div><span>Low priority</span><strong>' + escHtml(String(model.riskRegister.filter(risk => deriveRiskBand(risk) === 'low').length)) + '</strong></div>',
    '</div>',
    '</div>',
    (strengthsObservedHtml ? '<div class="client-report-section-eyebrow client-report-section-eyebrow-spaced">Strengths</div><section class="client-report-executive-summary">' + strengthsObservedHtml + '</section>' : ''),
    '</section>',
    '<section class="client-report-page client-report-page-break">',
    '<div class="client-report-risk-grid">' + riskCardsHtml + '</div>',
    '</section>',
  ];

  if (!generatePreviewOnly) {
    parts.push(
      '<section class="client-report-page">',
      '<div class="client-report-section-eyebrow">Appendix</div>',
      '<section class="client-report-appendix-section">',
      '<h3>' + escHtml(model.appendix.groupCountLabel || 'Largest Schedule Sections') + '</h3><ol class="client-report-ranked-list">' + appendixGroups + '</ol>',
      '</section>',
      '<section class="client-report-appendix-section client-report-supporting-narrative"><h3>Supporting Analysis</h3>' + renderSupportingAnalysisHtml(appendixAnalysisWithoutExecutiveBrief) + '</section>',
      '</section>',
    );
  }

  parts.push('</article>');
  return parts.join('');
}

// ---------------------------------------------------------------------------
// Cover page
// ---------------------------------------------------------------------------

function renderCover(model: ClientReportModel): string {
  const config = gameplanProductConfiguration();
  const logoUrl = `/Content/images/${config.productCode}-logo-with-name-thick-onblack.svg`;
  return [
    '<section class="client-report-page client-report-cover client-report-page-break">',
    '<div class="client-report-cover-top-rule"></div>',
    '<div class="client-report-cover-body">',
    '<div class="client-report-brand-row">',
    '<img class="client-report-brand-logo" src="' + escHtml(logoUrl) + '" alt="' + escHtml(config.productName) + '">',
    '</div>',
    '<div class="client-report-brand-report-title">Risk Report</div>',
    '<h1>' + escHtml(model.project.name) + '</h1>',
    '<dl class="client-report-cover-meta">',
    '<div><dt>Prepared</dt><dd>' + escHtml(formatPreparedDate(model.generatedAt)) + '</dd></div>',
    '<div><dt>Source</dt><dd>' + escHtml(model.project.sourceFile || 'Not available') + '</dd></div>',
    '</dl>',
    '<div class="client-report-cover-site">WWW.BUILDERPLAN.COM</div>',
    '</div>',
    '</section>',
  ].join('');
}

// ---------------------------------------------------------------------------
// Risk cards
// ---------------------------------------------------------------------------

function renderTopRiskCard(risk: NormalizedRisk): string {
  return [
    '<article class="client-report-risk-card">',
    '<div class="client-report-risk-card-top">',
    '<span class="client-report-risk-id">' + escHtml(risk.id) + '</span>',
    '<span class="client-report-chip client-report-chip-' + escHtml(deriveRiskBand(risk)) + '">' + escHtml(priorityLabel(risk)) + '</span>',
    '</div>',
    '<h3>' + escHtml(risk.title) + '</h3>',
    '<dl class="client-report-risk-meta">',
    '<div><dt>Likelihood</dt><dd>' + escHtml(formatRiskLevel(risk.likelihood)) + '</dd></div>',
    '<div><dt>Impact</dt><dd>' + escHtml(formatRiskLevel(risk.impact)) + '</dd></div>',
    '</dl>',
    '<div class="client-report-risk-detail-row"><div class="client-report-risk-detail-label">Description</div><div class="client-report-risk-detail-value">' + escHtml(risk.description || 'No description was provided.') + '</div></div>',
    '<div class="client-report-risk-affected-row"><div class="client-report-risk-affected-label">Affected Tasks</div><div class="client-report-risk-affected-value">' + formatAffectedTasks(risk.affectedTasks) + '</div></div>',
    '<div class="client-report-mitigation"><div class="client-report-risk-detail-label">Mitigation</div><div class="client-report-risk-detail-value">' + escHtml(risk.mitigation || 'No mitigation was provided.') + '</div></div>',
    '</article>',
  ].join('');
}

function priorityLabel(risk: NormalizedRisk): string {
  return formatRiskBand(deriveRiskBand(risk)).toUpperCase() + ' PRIORITY';
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatRiskLevel(value: string | undefined): string {
  if (!value) return '';
  const text = String(value).toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatRiskBand(value: string): string {
  return String(value)
    .split('-')
    .map(part => part ? part.charAt(0).toUpperCase() + part.slice(1) : part)
    .join('-');
}

function formatPreparedDate(value: string | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const formatter = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return formatter.format(date);
}

function formatAffectedTasks(items: string[] | undefined): string {
  if (!Array.isArray(items) || !items.length) return 'Not specified';
  const visibleItems = items.filter(Boolean).slice(0, 10);
  const hiddenCount = Math.max(items.filter(Boolean).length - visibleItems.length, 0);
  const chips = visibleItems.map(item =>
    '<span class="client-report-task-chip">' + escHtml(item) + '</span>',
  ).join('');
  if (!hiddenCount) return '<div class="client-report-task-chip-list">' + chips + '</div>';
  return '<div class="client-report-task-chip-list">' + chips + '<span class="client-report-task-chip client-report-task-chip-more">+' + escHtml(String(hiddenCount)) + ' more</span></div>';
}

function formatLongestChainValue(value: string): string {
  const text = String(value || '').trim();
  const match = text.match(/^(\d+)\s+(.+)$/i);
  if (!match) return escHtml(text);
  return '<span class="client-report-kpi-value-primary">' + escHtml(match[1]) + '</span><span class="client-report-kpi-value-secondary">' + escHtml(match[2]) + '</span>';
}

function simplifyCriticalPathCoverageNote(value: string): string {
  return String(value || '')
    .replace(/\bgrouping\b/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// ---------------------------------------------------------------------------
// Supporting-analysis markdown → HTML
// ---------------------------------------------------------------------------

function formatSupportingAnalysis(value: string | undefined): string {
  if (!value) return '';
  return String(value)
    .replace(/^\s*##\s*Analysis\s*/i, '')
    .trim();
}

function renderExecutiveBriefSection(model: ClientReportModel, extractedText: string): string {
  const extracted = formatSupportingAnalysis(extractedText);
  if (extracted) {
    return renderSupportingAnalysisHtml(extracted);
  }

  if (!model || !model.executiveSummary) {
    return '';
  }

  const summary = model.executiveSummary;
  const parts: string[] = [];

  if (summary.headline) {
    parts.push('<p>' + escHtml(summary.headline) + '</p>');
  }

  const keyPoints = Array.isArray(summary.keyPoints) ? summary.keyPoints.filter(Boolean) : [];
  if (keyPoints.length) {
    parts.push('<p>' + escHtml(keyPoints.join(' ')) + '</p>');
  }

  return parts.join('');
}

function renderSupportingAnalysisHtml(value: string | undefined): string {
  const text = formatSupportingAnalysis(value);
  if (!text) {
    return '<p>No supporting text was saved with the latest intelligence report.</p>';
  }

  const lines = text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map(line => line.trimEnd());

  const parts: string[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];

  function flushParagraph(): void {
    if (!paragraphLines.length) return;
    parts.push('<p>' + escHtml(paragraphLines.join(' ').trim()) + '</p>');
    paragraphLines = [];
  }

  function flushList(): void {
    if (!listItems.length) return;
    parts.push(
      '<ul class="client-report-analysis-list">' +
      listItems.map(item => '<li>' + escHtml(item) + '</li>').join('') +
      '</ul>',
    );
    listItems = [];
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    if (/^---+$/.test(line)) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = line.match(/^#{1,6}\s*(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const cleanedHeading = cleanAnalysisHeading(headingMatch[1].trim());
      if (cleanedHeading.toLowerCase() !== 'supporting analysis') {
        parts.push('<h4>' + escHtml(cleanedHeading) + '</h4>');
      }
      continue;
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      flushParagraph();
      listItems.push(bulletMatch[1].trim());
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();

  return parts.join('');
}

function cleanAnalysisHeading(text: string): string {
  return String(text || '')
    .replace(/^\s*\d+[\.)]\s*/, '')
    .replace(/\s*\((?:non-technical|technical)\)\s*$/i, '')
    .replace(/\s*\(what is structurally helpful\)\s*$/i, '')
    .trim();
}

function extractSupportingAnalysisSection(value: string | undefined, headingPrefix: string): string {
  const text = formatSupportingAnalysis(value);
  if (!text) return '';

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const target = String(headingPrefix || '').trim().toLowerCase();
  const collected: string[] = [];
  let inSection = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    const headingMatch = line.match(/^#{1,6}\s*(.+)$/);
    if (headingMatch) {
      const cleanedHeading = cleanAnalysisHeading(headingMatch[1]).toLowerCase();
      if (inSection) break;
      if (cleanedHeading.startsWith(target)) {
        inSection = true;
        continue;
      }
    }

    if (inSection) {
      collected.push(rawLine);
    }
  }

  return collected.join('\n').trim();
}

function removeSupportingAnalysisSection(value: string | undefined, headingPrefix: string): string {
  const text = formatSupportingAnalysis(value);
  if (!text) return '';

  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const target = String(headingPrefix || '').trim().toLowerCase();
  const kept: string[] = [];
  let skipping = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    const headingMatch = line.match(/^#{1,6}\s*(.+)$/);
    if (headingMatch) {
      const cleanedHeading = cleanAnalysisHeading(headingMatch[1]).toLowerCase();
      if (skipping && cleanedHeading) {
        skipping = false;
      }
      if (cleanedHeading.startsWith(target)) {
        skipping = true;
        continue;
      }
    }

    if (!skipping) {
      kept.push(rawLine);
    }
  }

  return kept.join('\n').trim();
}
